// SIMPLE ADMIN EXCEL EDITOR - Direct data creation and AWS storage
import React, { useState } from 'react';
import { simpleExcelStorage, SimpleExcelData } from '../services/simpleExcelStorage';
import { useAuth } from '../context/AuthContext';

interface SimpleAdminExcelProps {
  onClose?: () => void;
}

const SimpleAdminExcel: React.FC<SimpleAdminExcelProps> = ({ onClose }) => {
  const { user } = useAuth();
  const [headers, setHeaders] = useState(['Particulars', 'Concerned Person', 'Status', 'Priority', 'Due Date']);
  const [rows, setRows] = useState<string[][]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const addRow = () => {
    setRows([...rows, Array(headers.length).fill('')]);
  };

  const updateCell = (rowIndex: number, colIndex: number, value: string) => {
    const newRows = [...rows];
    if (!newRows[rowIndex]) {
      newRows[rowIndex] = Array(headers.length).fill('');
    }
    newRows[rowIndex][colIndex] = value;
    setRows(newRows);
    setSaved(false);
  };

  const updateHeader = (colIndex: number, value: string) => {
    const newHeaders = [...headers];
    newHeaders[colIndex] = value;
    setHeaders(newHeaders);
    setSaved(false);
  };

  const saveData = async () => {
    if (!user) {
      alert('Please login to save data');
      return;
    }

    setSaving(true);
    try {
      console.log('💾 SimpleAdminExcel: Saving Excel data...');

      // Create Excel data structure
      const excelData: SimpleExcelData = {
        id: `excel_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        headers: [...headers],
        rows: rows.map(row => [...row]), // Deep copy
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: user.uid,
        assignedTo: [] // Will be populated based on "Concerned Person" column
      };

      // Extract assigned users from "Concerned Person" column
      const assignedUsers = new Set<string>();
      rows.forEach(row => {
        const concernedPersonIndex = headers.findIndex(h => h.toLowerCase().includes('concerned person'));
        if (concernedPersonIndex >= 0 && row[concernedPersonIndex]) {
          // Simple user extraction - you can make this more sophisticated
          const userValue = row[concernedPersonIndex].toLowerCase();
          if (userValue.includes('john')) assignedUsers.add('john');
          if (userValue.includes('jane')) assignedUsers.add('jane');
          if (userValue.includes('mike')) assignedUsers.add('mike');
          // Add more users as needed
        }
      });

      excelData.assignedTo = Array.from(assignedUsers);

      console.log('💾 SimpleAdminExcel: Excel data prepared:', excelData);

      // Save to AWS S3
      const result = await simpleExcelStorage.saveExcelData(excelData);

      if (result.success) {
        setSaved(true);
        alert('✅ Data saved to AWS successfully!\nUsers will see this exact data in their dashboard.');
        console.log('✅ SimpleAdminExcel: Data saved successfully');
      } else {
        alert(`❌ Failed to save data: ${result.error}`);
        console.error('❌ SimpleAdminExcel: Save failed:', result.error);
      }

    } catch (error) {
      console.error('❌ SimpleAdminExcel: Error saving data:', error);
      alert(`❌ Error saving data: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setSaving(false);
    }
  };

  const addColumn = () => {
    setHeaders([...headers, `Column ${headers.length + 1}`]);
    setRows(rows.map(row => [...row, '']));
    setSaved(false);
  };

  const deleteColumn = (colIndex: number) => {
    if (headers.length <= 1) return; // Keep at least one column
    
    setHeaders(headers.filter((_, index) => index !== colIndex));
    setRows(rows.map(row => row.filter((_, index) => index !== colIndex)));
    setSaved(false);
  };

  const deleteRow = (rowIndex: number) => {
    setRows(rows.filter((_, index) => index !== rowIndex));
    setSaved(false);
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>📊 Simple Admin Excel Editor</h2>
        {onClose && (
          <button 
            onClick={onClose} 
            style={{ padding: '10px 20px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: '4px' }}
          >
            ❌ Close
          </button>
        )}
      </div>

      <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#e8f5e8', borderRadius: '4px' }}>
        <strong>💡 Simple Method:</strong> Enter data → Click Save → Users see exact same data
      </div>

      {/* Headers */}
      <div style={{ display: 'flex', marginBottom: '10px', alignItems: 'center' }}>
        {headers.map((header, index) => (
          <div key={index} style={{ display: 'flex', alignItems: 'center' }}>
            <input
              value={header}
              onChange={(e) => updateHeader(index, e.target.value)}
              style={{ 
                width: '120px', 
                margin: '2px', 
                padding: '8px', 
                border: '1px solid #ddd',
                backgroundColor: '#f0f0f0',
                fontWeight: 'bold'
              }}
            />
            <button
              onClick={() => deleteColumn(index)}
              style={{ 
                margin: '2px', 
                padding: '4px 8px', 
                backgroundColor: '#ff4444', 
                color: 'white', 
                border: 'none',
                borderRadius: '2px',
                fontSize: '12px'
              }}
              disabled={headers.length <= 1}
            >
              ✕
            </button>
          </div>
        ))}
        <button 
          onClick={addColumn}
          style={{ 
            margin: '2px', 
            padding: '8px 12px', 
            backgroundColor: '#4CAF50', 
            color: 'white', 
            border: 'none',
            borderRadius: '4px'
          }}
        >
          ➕ Add Column
        </button>
      </div>

      {/* Data Rows */}
      {rows.map((row, rowIndex) => (
        <div key={rowIndex} style={{ display: 'flex', marginBottom: '5px', alignItems: 'center' }}>
          {row.map((cell, colIndex) => (
            <input
              key={colIndex}
              value={cell}
              onChange={(e) => updateCell(rowIndex, colIndex, e.target.value)}
              style={{ 
                width: '120px', 
                margin: '2px', 
                padding: '8px', 
                border: '1px solid #ddd',
                borderRadius: '2px'
              }}
              placeholder={headers[colIndex]}
            />
          ))}
          <button
            onClick={() => deleteRow(rowIndex)}
            style={{ 
              margin: '2px', 
              padding: '4px 8px', 
              backgroundColor: '#ff4444', 
              color: 'white', 
              border: 'none',
              borderRadius: '2px',
              fontSize: '12px'
            }}
          >
            ✕
          </button>
        </div>
      ))}

      {/* Action Buttons */}
      <div style={{ marginTop: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
        <button 
          onClick={addRow} 
          style={{ 
            padding: '10px 20px', 
            backgroundColor: '#2196F3', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          ➕ Add Row
        </button>
        
        <button 
          onClick={saveData} 
          disabled={saving}
          style={{ 
            padding: '10px 20px', 
            backgroundColor: saving ? '#ccc' : '#4CAF50', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px',
            cursor: saving ? 'not-allowed' : 'pointer'
          }}
        >
          {saving ? '💾 Saving...' : '💾 Save to AWS'}
        </button>

        {saved && (
          <span style={{ color: '#4CAF50', fontWeight: 'bold' }}>
            ✅ Saved successfully!
          </span>
        )}
      </div>

      {/* Instructions */}
      <div style={{ marginTop: '30px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
        <h3>📋 Instructions:</h3>
        <ol>
          <li><strong>Headers:</strong> Click on header names to edit them (e.g., "Particulars", "Concerned Person")</li>
          <li><strong>Data:</strong> Click in cells to enter data</li>
          <li><strong>User Assignment:</strong> Put user names in "Concerned Person" column</li>
          <li><strong>Save:</strong> Click "Save to AWS" to store data</li>
          <li><strong>Result:</strong> Users will see exact same data in their dashboard</li>
        </ol>
      </div>
    </div>
  );
};

export default SimpleAdminExcel;
